import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Issue #1344 (D57, D331) — save before the coach leaves, shared by Game plan & notes and On the day.
 *
 * A screen with typed changes must not lose them when the coach goes. This hook owns the three
 * things both screens did by hand: whether there is anything unsaved, one save at a time, and the
 * hold that keeps a leave from acting until the server has the changes.
 *
 * The screen calls `edited()` on every change, and passes `save`: an async function that sends the
 * changes and THROWS when the server refuses (it also draws its own saving and failed states). It
 * runs its other saves (Save as draft, Publish team, Save today's changes) through `persist(task)`
 * so the same bookkeeping applies.
 *
 * Leaves covered while something is unsaved:
 * - any in-app link (`a[href]` starting with "/"): save, then navigate there. A modified click (a
 *   new tab), a download and an address outside the app are left to the browser.
 * - any control marked `data-leave` (sign out, the demo "Switch to ...", the account-menu role
 *   switch): the press is held, the changes are saved, then the same press is made again and the
 *   control acts as it always did. Nothing is imported from, or changed in, the control.
 * - a reload or a tab close: the browser's own prompt (no custom text; browsers do not allow one).
 *
 * A failed save keeps the coach where they are: the leave does not act, and every change stays. A
 * press while a save is under way is held and sends nothing twice. An edit made while a save is in
 * flight is not counted as saved, so the leave waits for it too: the next press saves it.
 *
 * Browser Back is NOT covered: the app mounts a <BrowserRouter>, and the router's blocker
 * (`useBlocker`) is available only under a data router. See #1344.
 *
 * @param {{ save: () => Promise<void>, beforeLeave?: () => void, onLeaveFailed?: () => void }} options
 *   `beforeLeave` runs just before a leave-save, so the screen can put the failed card in the right view.
 *   `onLeaveFailed` runs after a leave-save fails (not after a screen's own save fails), so the screen can
 *   bring the failed card into view for a coach who pressed from the top of the page (#1344).
 */
export default function useSaveBeforeLeave({ save, beforeLeave, onLeaveFailed } = {}) {
  const navigate = useNavigate();
  // `edits` counts the changes, `clean` is the count the server has.
  const edits = useRef(0);
  const clean = useRef(0);
  const saving = useRef(false);
  const latest = useRef({});
  latest.current = { save, beforeLeave, onLeaveFailed, navigate };

  const isDirty = useCallback(() => edits.current !== clean.current, []);
  const edited = useCallback(() => {
    edits.current += 1;
  }, []);
  const reset = useCallback(() => {
    edits.current = 0;
    clean.current = 0;
  }, []);

  // One save, for a screen's own buttons and for leaving. True when the server has the changes as
  // they were when the save began. The task throws on failure; the screen draws that.
  const persist = useCallback(async (task) => {
    if (saving.current) return false;
    saving.current = true;
    const sent = edits.current;
    try {
      await (task || latest.current.save)();
      clean.current = sent;
      return true;
    } catch {
      return false;
    } finally {
      saving.current = false;
    }
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (e.defaultPrevented) return;
      const control = e.target?.closest?.("[data-leave]");
      if (control) {
        if (!isDirty()) return;
        e.preventDefault();
        // The control's own handler must not run until the changes are saved.
        e.stopPropagation();
        if (saving.current) return;
        latest.current.beforeLeave?.();
        // Pressed again once the server has the changes, the control finds nothing unsaved and acts.
        persist().then((ok) => {
          if (!ok) latest.current.onLeaveFailed?.();
          else if (!isDirty() && control.isConnected) control.click();
        });
        return;
      }
      if (e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target?.closest?.("a[href]");
      if (!a || (a.target && a.target !== "_self") || a.hasAttribute("download")) return;
      const href = a.getAttribute("href");
      if (!href || !href.startsWith("/") || href.startsWith("//")) return;
      if (!isDirty()) return;
      e.preventDefault();
      if (saving.current) return;
      latest.current.beforeLeave?.();
      // Followed only when the server has the changes as they are now: an edit made during the save keeps the coach here.
      persist().then((ok) => {
        if (!ok) latest.current.onLeaveFailed?.();
        else if (!isDirty()) latest.current.navigate(href);
      });
    };
    const onUnload = (e) => {
      if (!isDirty()) return;
      e.preventDefault();
      e.returnValue = ""; // the browser's own prompt; older engines need this set
    };
    document.addEventListener("click", onClick, true);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [isDirty, persist]);

  return { edited, reset, persist, isDirty, isSaving: () => saving.current };
}
